import { Icon } from "@mirage-web/icons";
import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { PermissionGuard } from "~/components/permission-guard";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { ChargeHistorySection } from "~/modules/credits/components/charge-history-section";
import { CreditGrantDialog } from "~/modules/credits/components/credit-grant-dialog";
import { lookupOrgByIdQuery } from "~/modules/lookup/queries";

const querySchema = z.object({
  sheet: z.enum(["grant"]).optional(),
});

export const Route = createFileRoute("/_dash/charges/organizations/$orgId")({
  component: RouteComponent,
  async loader({ params, context }) {
    const { queryClient } = context;

    if (!params.orgId) {
      throw new Error("Organization ID is required");
    }

    queryClient.prefetchQuery(
      lookupOrgByIdQuery.queryOptions({ pathParams: { orgId: params.orgId } })
    );
  },
  validateSearch: querySchema,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { orgId } = Route.useParams();
  const search = Route.useSearch();

  const { data: org } = lookupOrgByIdQuery.useSuspenseQuery({
    pathParams: { orgId },
  });

  return (
    <PermissionGuard permission="orgs:view">
      <div className="space-y-6">
        {search.sheet == "grant" && (
          <CreditGrantDialog
            onClose={() => navigate({ search: (old) => ({ ...old, sheet: undefined }) })}
            preset={{ type: "org", id: orgId }}
          />
        )}

        <div className="flex">
          <div className="flex-1">
            <Link
              to="/organizations/view/$orgId"
              params={{ orgId }}
              className="text-sm text-muted-foreground hover:underline"
            >
              ← Back to Organizations
            </Link>
          </div>
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Icon name="options" className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <Link
                  to="/charges/organizations/$orgId"
                  params={{ orgId }}
                  search={{ sheet: "grant" }}
                  className="text-sm text-primary hover:underline"
                >
                  <DropdownMenuItem>
                    <Icon name="person" className="h-4 w-4 mr-2" />
                    Grant Credits
                  </DropdownMenuItem>
                </Link>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-semibold">{org?.org.orgDetails.name} charge history</h2>
        </div>

        <ChargeHistorySection subject={{ orgId }} />
      </div>
    </PermissionGuard>
  );
}
